import { Injectable } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { QueueAgent } from '@app/core/interfaces/c5/acd/queue-agent';
import { Role } from '@app/core/interfaces/c5/acd/role';
import { Announcement } from '@app/core/interfaces/c5/announcement';
import { ClientLoggingConfig } from '@app/core/interfaces/c5/client-logging-config';
import { Device } from '@app/core/interfaces/c5/device';
import { FeatureCode } from '@app/core/interfaces/c5/feature-code';
import { FeatureName } from '@app/core/interfaces/c5/feature-name';
import { PhoneBookContact } from '@app/core/interfaces/c5/phone-book-contact';
import { PhoneNumber } from '@app/core/interfaces/c5/phone-number';
import { RoomGroup } from '@app/core/interfaces/c5/room-group';
import { TimeTable } from '@app/core/interfaces/c5/time-table';
import { EndUserData } from '@app/core/interfaces/end-user-data';
import { Enterprise } from '@app/core/interfaces/enterprise';
import { EnterpriseOptionType } from '@app/core/interfaces/enterprise-option-type';
import { FeatureProfile } from '@app/core/interfaces/feature-profile';
import { SiteCommon } from '@app/core/interfaces/site-common';
import { Domain } from '@app/core/models/domain';
import { SecurityPermissionName } from '@app/core/models/permission';
import { Registration } from '@app/core/models/registration';
import { AcdRepository } from '@app/core/repositories/acd.repository';
import { AnnouncementRepository } from '@app/core/repositories/announcement.repository';
import { CallCenterRepository } from '@app/core/repositories/call-center.repository';
import { ClientLoggingConfigRepository } from '@app/core/repositories/client-log-config-repository.service';
import { DeviceRepository } from '@app/core/repositories/device.repository';
import { EndUserRepository } from '@app/core/repositories/end-user.repository';
import { FeatureProfileRepository } from '@app/core/repositories/feature-profile.repository';
import { FeatureRepository } from '@app/core/repositories/feature.repository';
import { PhoneBookRepository } from '@app/core/repositories/phone-book-repository';
import { PhoneNumberRepository } from '@app/core/repositories/phone-number.repository';
import { RegistrationRepository } from '@app/core/repositories/registration.repository';
import { RoomGroupRepository } from '@app/core/repositories/room-group.repository';
import { SiteRepository } from '@app/core/repositories/site.repository';
import { TimeTableRepository } from '@app/core/repositories/time-table.repository';
import {
  ApplicationEventService,
  EndUserChangedEvent,
  EndUserFeatureProfileChanged,
  OptionalFeatureChangedEvent,
  TrunkUsersChangedEvent,
  UserAssignedToTeamsTrunkEvent,
} from '@app/core/services/application-events';
import { RouterService } from '@app/routing/router.service';
import { CallCenterEndUserContext } from '@app/shared/models/call-center-end-user-context';
import { CachedHelperService } from '@app/shared/services/cached-helper.service';
import { EnterpriseContextService } from '@app/shared/services/context/enterprise-context.service';
import { Cached, cachedGroup } from '@app/utils/cached';
import { wrapError } from '@app/utils/rxjs';
import { Authorization } from '@business/end-user/models/authorization';
import { Features } from '@business/feature/interfaces/features';
import { combineLatest, Observable, of } from 'rxjs';
import { first, map, mergeMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class EndUserContextService {
  private enterpriseId: string | null = null;
  private userId: string | null = null;

  private _callCenter: Cached<CallCenterEndUserContext> | null = null;

  get callCenter(): Cached<CallCenterEndUserContext | null> {
    if (this.authenticationService.userIsEndUser()) {
      this._callCenter ??= cachedGroup(
        [this.endUser],
        ([endUser]) =>
          endUser.isCallCenterAgent
            ? this.callCenterRepository.getEndUserAgents().pipe(
                map((agents) => this.mapCallCenterAgents(agents)),
                wrapError('end-user-context-service:call-center')
              )
            : of(null),
        {
          id: 'end-user-context:call-center',
        }
      );
    }

    return this._callCenter;
  }

  private _acdAgents: Cached<QueueAgent[]> | null = null;

  get acdAgents(): Cached<QueueAgent[]> {
    this._acdAgents ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => this.acdRepository.listAllEndUserAgents(),
      id: ([userId]) => `user:${userId}:acd-agents`,
      rememberTtl: 10_000,
      routeParameters: ['userId'],
    });

    return this._acdAgents;
  }

  private _activeFeatureProfile: Cached<FeatureProfile> | null = null;

  get activeFeatureProfile(): Cached<FeatureProfile> {
    this._activeFeatureProfile ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => this.featureProfileRepository.getActiveFeatureProfile(),
      id: ([userId]) => `user:${userId}:active-feature-profile`,
      rememberTtl: 10_000,
      routeParameters: ['userId'],
    });

    return this._activeFeatureProfile;
  }

  /**
   * Will check in the end-user feature-profile if the feature is included and active (in case it is optional)
   * @param featureName TODO `featureName` needs to be an enum where all user features are enumerated, not only C5 user features
   * @returns emits latest result based on end-user feature-profile (`this.activeFeatureProfile`)
   */
  public hasActiveUserFeature$(featureName: FeatureName | string): Observable<boolean> {
    return this.activeFeatureProfile.data$.pipe(
      first(),
      map((fp: FeatureProfile) => {
        const fs = fp.features ?? [];
        return fs.some((f) => {
          if (f.name === featureName) {
            const active = f.optional == null || f.optional === false || f.userOptionalFeatureActive === true;
            return active;
          }
          return false;
        });
      })
    );
  }

  /**
   * For an end-user it checks if it can use rich presence.
   * First, the enterprise must have RichPresenceService activated and the end-user is a user with device,
   * and then the user features are checked for the active `RICH_PRESENCE` feature.
   * @returns emits the latest result based on enterprise options and end-user data
   */
  public canUseRichPresence$(): Observable<boolean> {
    const isEndUserWithDeviceAndHasRichPresenceFeatureActive = combineLatest([
      this.enterpriseContextService.enterpriseOptions.data$,
      this.endUser.data$,
    ]).pipe(
      mergeMap(([enterpriseOptions, endUserData]) => {
        const isRichPresenceServiceEnabled = enterpriseOptions.some(
          (eo) => eo.name === EnterpriseOptionType.RichPresenceService
        );
        if (isRichPresenceServiceEnabled && endUserData.user.auth === Authorization.FULL) {
          return this.hasActiveUserFeature$('RICH_PRESENCE').pipe(first());
        }
        return of(false);
      })
    );

    return isEndUserWithDeviceAndHasRichPresenceFeatureActive;
  }

  /**
   * Presence profile mode active or inactive on the end-user.
   * @returns the value of `presenceProfileActive` flag of the "local end-user data".
   */
  public hasPresenceProfilesModeActivated$(): Observable<boolean> {
    return this.endUser.data$.pipe(map((eud) => eud.localData?.user.presenceProfileActive ?? false));
  }

  private _announcements: Cached<Announcement[]> | null = null;

  get announcements(): Cached<Announcement[]> {
    this._announcements ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => this.announcementRepository.list(),
      id: ([userId]) => `user:${userId}:announcements`,
      rememberTtl: 10_000,
      routeParameters: ['userId'],
    });

    return this._announcements;
  }

  private _clientLogConfig: Cached<ClientLoggingConfig[]> | null = null;

  get clientLogConfig(): Cached<ClientLoggingConfig[]> {
    this._clientLogConfig ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => {
        return this.clientLogConfigRepository.list().pipe(wrapError('end-user-context:client-log-config'));
      },
      id: ([userId]) => `user:${userId}:client-log-config`,
      routeParameters: ['userId'],
    });

    return this._clientLogConfig;
  }

  private _cstaDevices: Cached<Device[]> | null = null;

  get cstaDevices(): Cached<Device[]> {
    this._cstaDevices ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () =>
        this.authenticationService.ifAnyPermission$(
          [SecurityPermissionName.VIEW_DEVICES, SecurityPermissionName.MANAGE_DEVICES],
          this.deviceRepository.listCstaDevices(),
          []
        ),
      id: ([userId]) => `user:${userId}:csta-devices`,
      rememberTtl: 10_000,
      routeParameters: ['userId'],
    });

    return this._cstaDevices;
  }

  private _devices: Cached<Device[]> | null = null;

  get devices(): Cached<Device[]> {
    this._devices ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () =>
        this.authenticationService.ifAnyPermission$(
          [SecurityPermissionName.VIEW_DEVICES, SecurityPermissionName.MANAGE_DEVICES],
          this.deviceRepository.list().pipe(wrapError('end-user-context-service:devices')),
          []
        ),
      id: ([userId]) => `user:${userId}:devices`,
      rememberTtl: 10_000,
      routeParameters: ['userId'],
    });

    return this._devices;
  }

  private _endUser: Cached<EndUserData> | null = null;

  get endUser(): Cached<EndUserData> {
    this._endUser ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => this.endUserRepository.get().pipe(wrapError('end-user-context-service:end-user')),
      id: ([userId]) => `user:${userId}:end-user-data`,
      rememberTtl: 10_000,
      routeParameters: ['userId'],
    });

    return this._endUser;
  }

  private _features: Cached<Features> | null = null;

  get features(): Cached<Features> {
    this._features ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => this.featureRepository.list().pipe(wrapError('end-user-context-service:features')),
      id: ([userId]) => `user:${userId}:features`,
      rememberTtl: 10_000,
      routeParameters: ['userId'],
    });

    return this._features;
  }

  private _featureCodes: Cached<FeatureCode[]> | null = null;

  get featureCodes(): Cached<FeatureCode[]> {
    this._featureCodes ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => this.featureRepository.listFeatureCodes(),
      id: ([userId]) => `user:${userId}:feature-codes`,
      rememberTtl: 10_000,
      routeParameters: ['userId'],
    });

    return this._featureCodes;
  }

  private _phoneBook: Cached<PhoneBookContact[]> | null = null;

  get phoneBook(): Cached<PhoneBookContact[]> {
    this._phoneBook ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () =>
        this.phoneBookRepository.list().pipe(wrapError('end-user-context-service:phone-book')),
      id: ([userId]) => `user:${userId}:phone-book`,
      rememberTtl: 10_000,
      routeParameters: ['userId'],
    });

    return this._phoneBook;
  }

  private _phoneNumbers: Cached<PhoneNumber[]> | null = null;

  get phoneNumbers(): Cached<PhoneNumber[]> {
    this._phoneNumbers ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () =>
        this.phoneNumberRepository.list().pipe(wrapError('end-user-context-service:phone-numbers')),
      id: ([userId]) => `user:${userId}:phone-numbers`,
      rememberTtl: 10_000,
      routeParameters: ['userId'],
    });

    return this._phoneNumbers;
  }

  private _registrations: Cached<Registration[]> | null = null;

  get registrations(): Cached<Registration[]> {
    this._registrations ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => this.registrationRepository.list(),
      id: ([userId]) => `user:${userId}:registrations`,
      routeParameters: ['userId'],
    });

    return this._registrations;
  }

  private _rooms: Cached<RoomGroup[]> | null = null;

  get rooms(): Cached<RoomGroup[]> {
    this._rooms ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => {
        return this.authenticationService.ifAnyPermission$(
          [SecurityPermissionName.VIEW_ROOMS],
          this.roomGroupRepository.listUserRooms(),
          []
        );
      },
      id: ([userId]) => `user:${userId}:room-groups`,
      rememberTtl: 10_000,
      routeParameters: ['userId'],
    });

    return this._rooms;
  }

  private _siteCommon: Cached<SiteCommon> | null = null;

  get siteCommon(): Cached<SiteCommon> {
    this._siteCommon ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => {
        return this.siteRepository.getSiteCommon();
      },
      id: 'site-common',
      routeParameters: ['userId'],
    });

    return this._siteCommon;
  }

  private _timeTables: Cached<TimeTable[]> | null = null;

  get timeTables(): Cached<TimeTable[]> {
    this._timeTables ??= this.cachedHelperService.routeAwareCached({
      dataProducer: ([userId]) => {
        return this.timeTableRepository.list(userId);
      },
      id: ([userId]) => `user:${userId}:time-tables`,
      routeParameters: ['userId'],
    });

    return this._timeTables;
  }

  get domain$(): Observable<Domain> {
    return this.endUser.data$.pipe(map((endUser) => endUser.domain));
  }

  get enterprise$(): Observable<Enterprise> {
    return this.enterpriseContextService.enterprise.data$;
  }

  get isActive(): boolean {
    return this.enterpriseId != null && this.userId != null;
  }

  constructor(
    private acdRepository: AcdRepository,
    private announcementRepository: AnnouncementRepository,
    private applicationEventService: ApplicationEventService,
    private authenticationService: AuthenticationService,
    private cachedHelperService: CachedHelperService,
    private callCenterRepository: CallCenterRepository,
    private deviceRepository: DeviceRepository,
    private endUserRepository: EndUserRepository,
    private enterpriseContextService: EnterpriseContextService,
    private featureProfileRepository: FeatureProfileRepository,
    private featureRepository: FeatureRepository,
    private phoneBookRepository: PhoneBookRepository,
    private phoneNumberRepository: PhoneNumberRepository,
    private registrationRepository: RegistrationRepository,
    private roomGroupRepository: RoomGroupRepository,
    private routerService: RouterService,
    private siteRepository: SiteRepository,
    private timeTableRepository: TimeTableRepository,
    private clientLogConfigRepository: ClientLoggingConfigRepository
  ) {
    this.applicationEventService.on$(TrunkUsersChangedEvent).subscribe(() => {
      if (this.routerService.currentRouteData.params.userId != null) {
        this._endUser?.reload();
      } else {
        this._endUser?.clear();
      }
    });

    this.applicationEventService.on$(EndUserChangedEvent).subscribe(() => {
      this._endUser?.reload(false);
    });

    this.applicationEventService.on$(OptionalFeatureChangedEvent).subscribe((event) => {
      if (this.routerService.currentRouteData.params.userId != null) {
        this._endUser?.reload();
        // Due to the fact that for de/activation of some user features the C5 provisioning might also change user devices
        // and their number mappings we need to reload the relevant data when that event occurs.
        // We could have done it selectively for e.g. MobileControl, MS Teams, WebControl, DesktopOperator, etc., however,
        // we do not know exactly what and when C5 changes relevant data we do it generally for all optional features.
        // if (event.payload.featureName === FeatureName.MICROSOFT_TEAMS) {
        this._devices?.reload();
        this._phoneNumbers?.reload();
        // }
      }
    });

    this.applicationEventService.on$(UserAssignedToTeamsTrunkEvent).subscribe(() => {
      if (this.routerService.currentRouteData.params.userId != null) {
        this._endUser?.reload();
        this._devices?.reload();
        this._phoneNumbers?.reload();
      }
    });

    this.authenticationService.onLogout$.subscribe(() => {
      this._callCenter = null;
    });

    this.applicationEventService.on$(EndUserFeatureProfileChanged).subscribe(() => {
      this.activeFeatureProfile.clear();
      this.features.clear();
      this.featureCodes.clear();
    });
  }

  private mapCallCenterAgents(agents: QueueAgent[]): CallCenterEndUserContext {
    return {
      agents,
      callCenterId: agents[0]?.queue.callCenterId,
      isSupervisor: agents.reduce((a, b) => a || b.role !== Role.AGENT, false),
    };
  }
}
